/* =========================================================================
   review-stats.js — keeps every store-wide rating and review count on the
   page tied to one number.

   There is exactly one source of truth: the store's Google Business Profile,
   served by the Worker as GET {proxyUrl}/reviews → { rating, total, googleUrl,
   reviews[] }. Liquid server-renders the last synced figures from the shop
   metafield `custom.google_reviews` (see snippets/review-stat.liquid) and
   renders nothing at all when that metafield is missing, so this file's job is
   to (a) refresh those figures against the live feed and (b) reveal any figure
   the server could not substantiate.

   No section may hold its own copy of a rating or a count. Sections declare
   what they want rendered and this file fills it in:

     [data-review-proxy]           the Worker base URL (first non-empty wins)
     [data-review-text]            template string; [rating] [count] [source]
                                   are substituted, then the element is shown
     [data-review-stars="svg"]     filled/empty SVG stars for the live rating
     [data-review-stars="glyph"]   the same, as ★ / ☆ text
     [data-review-hide-until-known] hidden until the feed answers

   Sections that need the review list itself (the modal, the carousel) share
   this file's single fetch via ShannonReviews.load() instead of issuing their
   own, so one page load costs one request no matter how many sections appear.
========================================================================= */
(function () {
  'use strict';

  var STAR_PATH =
    '<path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>';

  function starsSvg(rating) {
    var filled = Math.round(Number(rating) || 0);
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out +=
        '<svg class="star' + (i > filled ? ' is-empty' : '') +
        '" viewBox="0 0 24 24" aria-hidden="true">' + STAR_PATH + '</svg>';
    }
    return out;
  }

  function starsGlyph(rating) {
    var filled = Math.round(Number(rating) || 0);
    return new Array(filled + 1).join('★') + new Array(6 - filled).join('☆');
  }

  function proxyUrl() {
    var nodes = document.querySelectorAll('[data-review-proxy]');
    for (var i = 0; i < nodes.length; i++) {
      var v = (nodes[i].getAttribute('data-review-proxy') || '').trim();
      if (v) return v.replace(/\/$/, '');
    }
    return '';
  }

  /* ---- one fetch per page, shared with every section that wants the list ----
     This settles to a PAYLOAD either way: a feed that is down resolves to
     { error: ..., reviews: [] } rather than rejecting. Subscribers then always
     run, and a section can tell "the feed is down" (say so) apart from "the
     feed answered with nothing" (which would be a claim about the business).
     A rejection here used to leave the reviews modal on "Loading reviews…"
     for good. */
  var pending = null;
  function load() {
    if (pending) return pending;
    var proxy = proxyUrl();
    pending = proxy
      ? fetch(proxy + '/reviews')
          .then(function (r) {
            return r.json().catch(function () { return {}; }).then(function (d) {
              if (!r.ok && !d.error) d.error = 'reviews_http_' + r.status;
              if (!Array.isArray(d.reviews)) d.reviews = [];
              return d;
            });
          })
          .catch(function () { return { error: 'reviews_unreachable', reviews: [] }; })
      : Promise.resolve({ error: 'reviews_not_configured', reviews: [] });
    return pending;
  }

  /* Subscribers can register before or after the fetch resolves. Until the
     data lands this is a plain array the page pushes callbacks onto; once it
     lands, push() runs them immediately instead. */
  var queue = window.ShannonReviewsQueue;
  if (!queue || typeof queue.push !== 'function') queue = [];

  var ShannonReviews = {
    load: load,
    starsSvg: starsSvg,
    starsGlyph: starsGlyph,
    /* Register a callback for the feed. Runs exactly once, with the payload —
       which on a bad day is { error, reviews: [] }. Check `error` before
       treating an empty list as the truth about the business. */
    ready: function (fn) { queue.push(fn); }
  };

  function applyText(data) {
    var rating = data.rating == null ? null : Number(data.rating).toFixed(1);
    var count = data.total ? Number(data.total).toLocaleString('en-US') : null;
    if (rating == null || count == null) return;

    var sourceEl = document.querySelector('[data-review-source]');
    var source = (sourceEl && sourceEl.getAttribute('data-review-source')) || 'Google reviews';

    document.querySelectorAll('[data-review-text]').forEach(function (el) {
      el.textContent = el
        .getAttribute('data-review-text')
        .replace(/\[rating\]/g, rating)
        .replace(/\[count\]/g, count)
        .replace(/\[source\]/g, source);
    });

    document.querySelectorAll('[data-review-stars]').forEach(function (el) {
      var mode = el.getAttribute('data-review-stars');
      el.innerHTML = mode === 'glyph' ? starsGlyph(data.rating) : starsSvg(data.rating);
      el.setAttribute('aria-label', 'Rated ' + rating + ' out of 5');
    });

    document.querySelectorAll('[data-review-hide-until-known]').forEach(function (el) {
      el.hidden = false;
    });
  }

  function drain(data) {
    applyText(data);
    var fns = queue.slice();
    queue.length = 0;
    /* From here on, late subscribers get the data straight away. */
    queue.push = function (fn) { try { fn(data); } catch (e) { /* one bad section must not stop the rest */ } };
    fns.forEach(function (fn) { try { fn(data); } catch (e) {} });
  }

  window.ShannonReviews = ShannonReviews;
  window.ShannonReviewsQueue = queue;

  /* load() always resolves. On an error payload applyText() finds no rating and
     leaves every unsubstantiated figure hidden, exactly as before. */
  load().then(drain);
})();
